import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useCurrentUser, useAccessToken } from '@/features/auth/state/auth'

const BASE_URL = import.meta.env.VITE_API_BASE_URL
const api = axios.create({ baseURL: BASE_URL })

// Hook para criar um novo produto
export function useCreateProduct() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (createProductDto: any) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.post('/products', createProductDto, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao criar produto')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })
}
// hooks/product.ts
export function useFetchProductsByTenants(idTenants: string[] | string | undefined) {
  const user = useCurrentUser()
  const token = useAccessToken()

  const enabled = Boolean(user && token && idTenants && (Array.isArray(idTenants) ? idTenants.length : idTenants))

  return useQuery({
    queryKey: ['products', ...(Array.isArray(idTenants) ? idTenants.sort() : [idTenants])],
    enabled,
    queryFn: async () => {
      if (!enabled) return []

      const ids = Array.isArray(idTenants) ? idTenants : [idTenants]

      const responses = await Promise.all(
        ids.map(id =>
          api.get(`/products/tenants/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      )
      // normaliza e achata tudo num único array
      return responses.flatMap(r =>
        (r.data?.data ?? []).map((p: any) => ({
          cnpj: p.id_tenant,
          razao_social: p.razao_social,
          id_product: p.id_product,
          typeServico: p.typeServico,
          typeRegime: p.typeRegime
        }))
      )
    }
  })
}

export function useFetchProduByTenantUser(id_tenant: string | undefined) {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useQuery({
    queryKey: ['products', id_tenant],
    queryFn: async () => {
      if (!user || !token) throw new Error('Usuário ou token ausente')

      const response = await api.get(`/products/tenant/${id_tenant}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data && Array.isArray(response.data.data)) {
        const products = response.data.data.map((product: any) => ({
          cnpj: product.id_tenant,
          id_product: product.id_product,
          typeServico: product.typeServico
        }))

        return products
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(user && token)
  })
}
// Hook para buscar todos os produtos
export function useFetchAllProducts() {
  const token = useAccessToken()

  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get('/products', {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(token)
  })
}

// Hook para atualizar um produto por ID
export function useUpdateProduct() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id, updateProductDto }: { id: string; updateProductDto: any }) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.patch(`/products/${id}`, updateProductDto, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data) {
        return response.data
      } else {
        throw new Error(`Erro ao atualizar produto com ID ${id}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })
}

export function useUpdateProductStatus() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.post(
        `/products/status/${id}/${status}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      if (response.data) {
        return response.data
      } else {
        throw new Error(`Erro ao atualizar produto com ID ${id}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })
}

// Hook para deletar um produto por ID
export function useDeleteProduct() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.delete(`/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data) {
        return response.data
      } else {
        throw new Error(`Erro ao deletar o produto com ID ${id}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })
}
